import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/Card.jsx';
import { superAdminApi } from '../../api/superAdmin.js';
import { Ticket, RefreshCw, CircleCheck, ChevronDown, ChevronRight, Phone, Wrench, Loader2 } from 'lucide-react';

/**
 * /admin/tickets — 超管支援工單佇列
 *   來源：經銷商診斷判為「系統端問題」時自動 / 手動回報開單。
 *   超管看得到 internalDetail（完整技術細節）；dealer 端只看到「已接手 TK-xxxx」。
 */
const SOURCE_LABEL = { 'dealer-diagnose': '自動偵測', 'dealer-reported': '經銷商回報' };
const CONF_STYLE = {
    high: 'bg-red-100 text-red-700',
    model: 'bg-amber-100 text-amber-700',
    user: 'bg-sky-100 text-sky-700',
};

export default function TicketsPage() {
    const { t } = useTranslation('superAdmin');
    const [status, setStatus] = useState('open');
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        superAdminApi.tickets(status)
            .then((r) => setTickets(r?.tickets || []))
            .catch(() => setTickets([]))
            .finally(() => setLoading(false));
    }, [status]);
    useEffect(load, [load]);

    const resolve = async (ticketId) => {
        setBusy(ticketId);
        try { await superAdminApi.resolveTicket(ticketId); load(); }
        catch (e) { alert(e.message); }
        finally { setBusy(null); }
    };

    const fmtTime = (ts) => {
        const ms = ts?._seconds ? ts._seconds * 1000 : (ts?.toMillis ? ts.toMillis() : ts);
        const d = ms ? new Date(ms) : null;
        return d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : '—';
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <Ticket size={18} className="text-accent-600" />
                <h1 className="heading-display text-xl font-semibold text-ink-900">{t('tickets.title')}</h1>
                <div className="ml-auto flex items-center gap-2">
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="rounded-md border border-cream-300 bg-white px-2.5 py-1 text-sm text-ink-800"
                    >
                        <option value="open">{t('tickets.statusOpen')}</option>
                        <option value="resolved">{t('tickets.statusResolved')}</option>
                        <option value="all">{t('tickets.statusAll')}</option>
                    </select>
                    <button onClick={load} className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-accent-700">
                        <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> {t('overview.refresh')}
                    </button>
                </div>
            </div>
            <p className="text-sm text-ink-500 max-w-2xl">{t('tickets.intro')}</p>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-ink-400 py-6"><Loader2 size={14} className="animate-spin" /> {t('tickets.loading')}</div>
            ) : tickets.length === 0 ? (
                <Card><div className="p-6 text-center text-sm text-ink-400">{t('tickets.empty')}</div></Card>
            ) : (
                <div className="space-y-2">
                    {tickets.map((tk) => {
                        const open = expanded === tk.ticketId;
                        return (
                            <Card key={tk.ticketId} padded={false}>
                                <div className="p-4">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button onClick={() => setExpanded(open ? null : tk.ticketId)} className="inline-flex items-center gap-1 text-ink-400 hover:text-accent-700">
                                            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        </button>
                                        <span className="font-mono text-xs font-semibold text-accent-700">{tk.ticketId}</span>
                                        <span className="inline-flex items-center gap-1 text-[11px] text-ink-500">
                                            {tk.kind === 'call' ? <Phone size={11} /> : <Wrench size={11} />}
                                            {tk.kind === 'call' ? t('tickets.kindCall') : t('tickets.kindBuilder')}
                                        </span>
                                        <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded ${CONF_STYLE[tk.confidence] || 'bg-cream-100 text-ink-500'}`}>
                                            {SOURCE_LABEL[tk.source] || tk.source} · {tk.confidence}
                                        </span>
                                        {tk.dealerName && <span className="text-[11px] text-ink-500">{tk.dealerName}</span>}
                                        {tk.dupCount > 1 && <span className="font-mono text-[10px] text-warn-600">×{tk.dupCount}</span>}
                                        {/* AI 工單助理狀態 */}
                                        {tk.resolvedBy === 'qubby-ai-agent' ? (
                                            <span className="rounded bg-success-50 px-1.5 py-0.5 text-[10px] font-medium text-success-700">{t('tickets.aiResolved')}</span>
                                        ) : tk.aiVerdict === 'needs-code' ? (
                                            <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">{t('tickets.aiNeedsCode')}</span>
                                        ) : tk.aiVerdict === 'not-system' ? (
                                            <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">{t('tickets.aiNotSystem')}</span>
                                        ) : tk.aiStatus === 'processing' && tk.status === 'open' ? (
                                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">{t('tickets.aiWorking')}</span>
                                        ) : null}
                                        {tk.reopenCount > 0 && (
                                            <span className="rounded bg-warn-50 px-1.5 py-0.5 text-[10px] font-medium text-warn-700">{t('tickets.reopened', { count: tk.reopenCount })}</span>
                                        )}
                                        <span className="ml-auto text-[11px] text-ink-400">{fmtTime(tk.createdAt)}</span>
                                        {tk.status === 'open' ? (
                                            <button
                                                onClick={() => resolve(tk.ticketId)}
                                                disabled={busy === tk.ticketId}
                                                className="inline-flex items-center gap-1 rounded-md border border-success-500/40 bg-success-50 px-2 py-0.5 text-[11px] text-success-700 hover:bg-success-100 disabled:opacity-40"
                                            >
                                                {busy === tk.ticketId ? <Loader2 size={11} className="animate-spin" /> : <CircleCheck size={11} />} {t('tickets.resolve')}
                                            </button>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-ink-400"><CircleCheck size={11} /> {t('tickets.resolved')}</span>
                                        )}
                                    </div>
                                    <div className="mt-2 text-sm text-ink-700">{tk.dealerFacingSummary || '—'}</div>
                                    {open && (
                                        <div className="mt-3 rounded-md bg-ink-900/[0.03] border border-cream-200 p-3 space-y-2">
                                            <div>
                                                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-1">{t('tickets.internalDetail')}</div>
                                                <pre className="whitespace-pre-wrap break-words text-xs text-ink-700 font-mono">{tk.internalDetail || '—'}</pre>
                                            </div>
                                            {tk.aiRootCause && (
                                                <div>
                                                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-1">{t('tickets.aiRootCause')}</div>
                                                    <pre className="whitespace-pre-wrap break-words text-xs text-ink-700 font-mono">{tk.aiRootCause}</pre>
                                                </div>
                                            )}
                                            {tk.aiStrategy && (
                                                <div>
                                                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-1">{t('tickets.aiStrategy')}</div>
                                                    <pre className="whitespace-pre-wrap break-words text-xs text-ink-700 font-mono">{tk.aiStrategy}</pre>
                                                </div>
                                            )}
                                            {tk.dealerProgress && (
                                                <div>
                                                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-1">{t('tickets.dealerProgress')}</div>
                                                    <div className="text-xs text-ink-700">{tk.dealerProgress}</div>
                                                </div>
                                            )}
                                            {tk.reopenNote && (
                                                <div>
                                                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-1">{t('tickets.reopenNote')}</div>
                                                    <div className="text-xs text-ink-700">{tk.reopenNote}</div>
                                                </div>
                                            )}
                                            <div>
                                                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400 mb-1">{t('tickets.locator')}</div>
                                                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
                                                    {[
                                                        [tk.kind === 'call' ? t('tickets.sessionId') : t('tickets.buildId'), tk.refId],
                                                        ['callId', tk.callId],
                                                        [t('tickets.avatar'), tk.avatarName ? `${tk.avatarName}${tk.avatarId ? ` (${tk.avatarId})` : ''}` : tk.avatarId],
                                                        [t('tickets.tenant'), tk.tenantName ? `${tk.tenantName} · ${tk.hostId || ''}` : tk.hostId],
                                                        ['region', tk.region],
                                                        [t('tickets.direction'), tk.direction],
                                                        [t('tickets.callTime'), tk.callStartedAt],
                                                    ].filter(([, v]) => v).map(([k, v]) => (
                                                        <div key={k} className="contents">
                                                            <span className="text-ink-400">{k}</span>
                                                            <span className="font-mono text-ink-700 break-all select-all">{String(v)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
